import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Send, Loader2 } from 'lucide-react';
import { API_URL } from '../lib/api';

const SUGGESTIONS = [
  'Mon mycélium devient vert, que faire ?',
  'Quel substrat pour des pleurotes ?',
  'Paramètres idéaux pour le shiitake ?',
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(localStorage.getItem('tf_chat_session') || (() => {
    const id = crypto.randomUUID();
    localStorage.setItem('tf_chat_session', id);
    return id;
  })()).current;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API_URL}/chat/history/${sessionId}`)
        .then((r) => r.json())
        .then((h) => {
          if (h.length) setMessages(h);
          else setMessages([{ role: 'assistant', content: "Bonjour, je suis MycoAI — l'assistant du laboratoire TerraFungi. Incubation, contamination, substrats : posez votre question." }]);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const payload = JSON.parse(line.slice(5));
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: 'Erreur de transmission. Réessayez.' };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: 'spring', stiffness: 200 }}
        onClick={() => setOpen(!open)}
        data-testid="myco-ai-widget-trigger"
        className="clip-frame-sm fixed bottom-24 right-4 z-[85] flex h-14 w-14 items-center justify-center border border-neon/50 bg-void/90 text-neon shadow-[0_0_30px_rgba(16,185,129,0.35)] backdrop-blur transition-all hover:bg-neon hover:text-void sm:bottom-6 sm:right-6"
        aria-label="Ouvrir MycoAI"
      >
        {open ? <X size={22} /> : <Bot size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="clip-frame fixed bottom-40 right-4 z-[85] flex h-[520px] w-[calc(100vw-2rem)] max-w-sm flex-col border border-neon/30 bg-panel shadow-[0_0_60px_rgba(16,185,129,0.2)] sm:bottom-24 sm:right-6"
            data-testid="myco-ai-panel"
          >
            <div className="flex items-center gap-3 border-b border-neon/20 bg-void/60 p-4">
              <span className="clip-frame-sm flex h-9 w-9 items-center justify-center bg-neon/15 text-neon">
                <Bot size={18} />
              </span>
              <div>
                <p className="font-display text-sm font-bold text-slate-100">MYCO//AI</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neon/80">Conseiller myciculture · en ligne</p>
              </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="myco-ai-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'clip-frame-sm bg-neon/15 text-slate-100'
                      : 'clip-frame-sm border border-white/8 bg-void/70 text-slate-300'
                  }`}>
                    {m.content || <Loader2 size={14} className="animate-spin text-neon" />}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="myco-ai-suggestion"
                    className="clip-frame-sm border border-cyber/30 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-cyber transition-colors hover:bg-cyber/10"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex gap-2 border-t border-white/8 p-3"
              data-testid="myco-ai-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Votre question…"
                data-testid="myco-ai-input"
                className="flex-1 border border-white/10 bg-void/60 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-neon/60 focus:outline-none"
              />
              <button
                type="submit"
                disabled={streaming}
                data-testid="myco-ai-send-button"
                className="clip-frame-sm flex h-10 w-10 items-center justify-center bg-neon text-void transition-colors hover:bg-cyber disabled:opacity-50"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
